import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "./../../images/cdl-pharma.png";
import { addLogoToPdf } from "./utils/PdfUtils";
import { getBackorderDisplayValue } from "../screens/protected/order/shared/BackorderHelper";

const formatPdfDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;
};

const getOrderCenterProtocols = (order) =>
  order?.Center?.Protocols?.map((protocol) => protocol.reference)
    .filter(Boolean)
    .join(", ") || "N/A";

const isShipmentOnBackorder = (shipment) =>
  Boolean(shipment?.onBackorder ?? shipment?.on_backorder);

const addBackorderMaterialBanner = (
  pdf,
  shipment,
  startY,
  { leftMargin = 10, rightMargin = 10 } = {},
) => {
  if (!isShipmentOnBackorder(shipment)) {
    return startY;
  }

  const pageWidth = pdf.internal.pageSize.getWidth();
  const bannerHeight = 9;
  const bannerGap = 3;
  const bannerWidth = pageWidth - leftMargin - rightMargin;

  pdf.setFillColor(255, 248, 223);
  pdf.setDrawColor(240, 180, 41);
  pdf.rect(leftMargin, startY, bannerWidth, bannerHeight, "FD");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(166, 106, 0);
  pdf.text("ON BACKORDER", leftMargin + bannerWidth / 2, startY + 6, {
    align: "center",
  });
  pdf.setTextColor(0, 0, 0);
  pdf.setDrawColor(0, 0, 0);
  pdf.setFont("helvetica", "normal");

  return startY + bannerHeight + bannerGap;
};

const getOrderAddress = (order) => {
  const addressParts = [
    order?.hospital,
    order?.ward,
    order?.building ? `Building: ${order.building}` : null,
    order?.street,
    order?.floor ? `Floor: ${order.floor}` : null,
    [order?.zipcode, order?.shippingAddress].filter(Boolean).join(" "),
  ];

  return addressParts.filter(Boolean).join("\n") || "N/A";
};

const addReceiptInstructions = (pdf, rectX, rectY, rectWidth) => {
  const horizontalPadding = 5;
  const lineHeight = 4.5;
  const textWidth = rectWidth - 2 * horizontalPadding;
  const introduction =
    "Upon receipt of the materials and/or documents, please:";
  const items = [
    "- Check the items received and verify the quantities.",
    "- Record the receipt either by completing this form and sending it to CDL Pharma at order@cdlpharma.com, or directly in the CDL PORTAL.",
    "- If the receipt is recorded in the CDL PORTAL, no further action is required.",
  ];

  pdf.setFont("helvetica", "bold");
  const introductionLines = pdf.splitTextToSize(introduction, textWidth);
  pdf.setFont("helvetica", "normal");
  const itemLines = pdf.splitTextToSize(items.join("\n"), textWidth);
  const rectHeight =
    11 + (introductionLines.length + itemLines.length) * lineHeight;

  pdf.rect(rectX, rectY, rectWidth, rectHeight);
  pdf.setFont("helvetica", "bold");
  pdf.text("Instructions:", rectX + horizontalPadding, rectY + 5);
  pdf.text(introductionLines, rectX + horizontalPadding, rectY + 10);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    itemLines,
    rectX + horizontalPadding,
    rectY + 10 + introductionLines.length * lineHeight,
  );

  return rectHeight;
};

export const printOrderDetails = (order, studyCode, centerNumber) => {
  const pdf = new jsPDF("p", "mm", "a4");
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageCenter = pageWidth / 2;
  const leftMargin = 10;
  const titleY = logoHeight + 30;
  const headerHeight = 12;

  pdf.setFontSize(12);
  pdf.setFillColor(173, 216, 230);
  pdf.rect(
    leftMargin,
    titleY - headerHeight,
    pageWidth - leftMargin * 2,
    headerHeight,
    "F",
  );
  pdf.text(`Order ${order?.orderNumber || "N/A"}`, pageCenter, titleY - 4, {
    align: "center",
  });

  const resolvedStudyCode =
    studyCode ||
    order?.Center?.Study?.cdlStudyCode ||
    order?.Center?.Study?.sponsorStudyCode ||
    "N/A";
  const resolvedCenterNumber =
    centerNumber || order?.Center?.centerNumber || "N/A";
  const labelStyle = { fontStyle: "bold", fillColor: [245, 245, 245] };
  const personName = (user) =>
    [user?.firstname, user?.name].filter(Boolean).join(" ") || "N/A";

  const detailRows = [
    [
      "Study code",
      resolvedStudyCode,
      "Center number",
      resolvedCenterNumber,
      "Center protocols",
      getOrderCenterProtocols(order),
    ],
    [
      "Contact name",
      order?.contactName || "N/A",
      "Contact email",
      order?.contactEmail || "N/A",
      "Contact phone",
      order?.contactPhone || "N/A",
    ],
    [
      "First supply",
      order?.isFirstSupply ? "Yes" : "No",
      "Demo kit",
      order?.isDemokit ? "Yes" : "No",
      "",
      "",
    ],
  ];

  pdf.autoTable({
    body: detailRows,
    startY: titleY + 6,
    margin: { left: leftMargin, right: leftMargin },
    styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index % 2 === 0) {
        Object.assign(data.cell.styles, labelStyle);
      }
    },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 35 },
      2: { cellWidth: 24 },
      3: { cellWidth: 32 },
      4: { cellWidth: 30 },
      5: { cellWidth: pageWidth - leftMargin * 2 - 146 },
    },
  });

  const detailTableY =
    pdf.lastAutoTable?.finalY || pdf.autoTable.previous?.finalY || titleY + 6;

  pdf.autoTable({
    body: [
      [
        "Shipping address",
        getOrderAddress(order),
        "Site comment",
        order?.comment || "None",
      ],
    ],
    startY: detailTableY + 4,
    margin: { left: leftMargin, right: leftMargin },
    styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" },
    didParseCell: (data) => {
      if (
        data.section === "body" &&
        (data.column.index === 0 || data.column.index === 2)
      ) {
        Object.assign(data.cell.styles, labelStyle);
      }
    },
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 68 },
      2: { cellWidth: 28 },
      3: { cellWidth: pageWidth - leftMargin * 2 - 128 },
    },
  });

  const supplies = Array.isArray(order?.Supplies) ? order.Supplies : [];
  const supplyRows =
    supplies.length > 0
      ? supplies.map((supply) => [
          supply?.Material?.type || "N/A",
          supply?.Material?.name || "N/A",
          String(supply?.quantity ?? "N/A"),
        ])
      : [["N/A", "No material order lines", "N/A"]];

  const lastTableY =
    pdf.lastAutoTable?.finalY || pdf.autoTable.previous?.finalY || titleY + 6;

  pdf.autoTable({
    head: [["Type", "Material", "Quantity"]],
    body: supplyRows,
    startY: lastTableY + 8,
    margin: { left: leftMargin, right: leftMargin },
    styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" },
    headStyles: { fillColor: [173, 216, 230], textColor: [0, 0, 0] },
    columnStyles: {
      0: { cellWidth: 36 },
      1: { cellWidth: pageWidth - leftMargin * 2 - 66 },
      2: { cellWidth: 30, halign: "right" },
    },
  });

  const productTableY =
    pdf.lastAutoTable?.finalY || pdf.autoTable.previous?.finalY || lastTableY;

  pdf.autoTable({
    head: [["Status", "Person", "Email", "Date"]],
    body: [
      [
        "Placed",
        personName(order?.placedBy),
        order?.placedBy?.email || "N/A",
        formatPdfDate(order?.orderDate),
      ],
      [
        "Confirmed",
        personName(order?.confirmedBy),
        order?.confirmedBy?.email || "N/A",
        formatPdfDate(order?.confirmationDate),
      ],
    ],
    startY: productTableY + 8,
    margin: { left: leftMargin, right: leftMargin },
    styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" },
    headStyles: { fillColor: [173, 216, 230], textColor: [0, 0, 0] },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 48 },
      2: { cellWidth: pageWidth - leftMargin * 2 - 138 },
      3: { cellWidth: 40 },
    },
  });

  pdf.save(`OrderDetails_${order?.orderNumber || order?.id || "order"}.pdf`);
};

export const printAor = (
  order,
  shipment,
  centerNumber,
  aors = [],
  shippedProductions = [],
) => {
  const pdf = new jsPDF("p", "mm", "a4");
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageCenter = pageWidth / 2;
  pdf.setFontSize(12);
  const text = "ACKNOWLEDGEMENT OF RECEIPT";
  const leftMargin = 10;
  const textHeight = 12;
  const titleY = logoHeight + 30;
  const leftColumn = 50;
  const headerWidth = pageWidth - leftMargin * 2;
  const lineHeight = 7;
  const study = order?.Center?.Study || {};
  const sponsorStudyCode =
    study.sponsorStudyCode || study.cdlStudyCode || "N/A";
  const sponsorName = study.sponsor?.name;
  const studyLine = sponsorName
    ? `${sponsorStudyCode} (${sponsorName})`
    : sponsorStudyCode;
  const addressParts = String(order?.shippingAddress || "").split(", ");
  const city = addressParts[0] || "N/A";
  const country = addressParts.slice(1).join(", ") || "N/A";
  const resolvedCenterNumber =
    centerNumber || order?.Center?.centerNumber || "N/A";
  const receiver = shipment?.receivedBy || {};
  const receiverName = [receiver.firstname, receiver.name]
    .filter(Boolean)
    .join(" ");

  pdf.setFillColor(173, 216, 230);
  pdf.rect(leftMargin, titleY - textHeight, headerWidth, textHeight, "F");

  const textY = titleY - textHeight + pdf.internal.getLineHeight() / 2;
  pdf.text(text, pageCenter, textY, { align: "center" });

  pdf.setFontSize(10);
  pdf.text(studyLine, pageCenter, logoHeight + 35, { align: "center" });
  pdf.text(
    `CDL Study Code: ${study.cdlStudyCode || "N/A"}`,
    pageCenter,
    logoHeight + 35 + lineHeight,
    { align: "center" },
  );

  pdf.text(`Order Number:`, leftMargin, logoHeight + 35 + 2 * lineHeight);
  pdf.text(order?.orderNumber || "N/A", leftColumn, logoHeight + 35 + 2 * lineHeight);

  pdf.text(`Center Number:`, leftMargin, logoHeight + 35 + 3 * lineHeight);
  pdf.text(resolvedCenterNumber, leftColumn, logoHeight + 35 + 3 * lineHeight);

  pdf.text(`Center Protocols:`, leftMargin, logoHeight + 35 + 4 * lineHeight);
  pdf.text(
    getOrderCenterProtocols(order),
    leftColumn,
    logoHeight + 35 + 4 * lineHeight,
  );

  pdf.text(`Hospital:`, leftMargin, logoHeight + 35 + 5 * lineHeight);
  pdf.text(order?.hospital || "N/A", leftColumn, logoHeight + 35 + 5 * lineHeight);

  pdf.text("City:", leftMargin, logoHeight + 35 + 6 * lineHeight);
  pdf.text(city, leftColumn, logoHeight + 35 + 6 * lineHeight);

  pdf.text(`Country:`, leftMargin, logoHeight + 35 + 7 * lineHeight);
  pdf.text(country, leftColumn, logoHeight + 35 + 7 * lineHeight);

  const rectX = leftMargin;
  const rectY = logoHeight + 87;
  const rectWidth = pageWidth - 2 * leftMargin;
  const rectHeight = addReceiptInstructions(pdf, rectX, rectY, rectWidth);

  const aorByMaterialId = new Map(
    aors.map((aor) => [String(aor.materialId), aor]),
  );
  const tableData = Array.isArray(order?.Supplies)
    ? order.Supplies.map((supply) => {
        const materialId = String(supply?.Material?.id);
        const matchingAor = aorByMaterialId.get(materialId);
        const shippingQuantity =
          shipment?.productions?.filter(
            (production) => String(production.materialId) === materialId,
          ).length || 0;

        return {
          materialName: supply?.Material?.name || "N/A",
          shippingQuantity,
          receivedQuantity: matchingAor?.quantityReceived ?? "",
          materialConform: Boolean(matchingAor?.materialConform),
          comment: matchingAor?.comment || "",
          backorderQuantity: getBackorderDisplayValue(
            supply,
            shippedProductions,
            "Complete",
          ),
        };
      })
    : [];
  const materialTableStartY = addBackorderMaterialBanner(
    pdf,
    shipment,
    rectY + rectHeight + 15,
  );

  pdf.autoTable({
    head: [
      [
        "Material Name",
        "Sent Quantity",
        "Received Quantity",
        "Material Conform?",
        "Backorder",
      ],
    ],
    body: tableData.map((item) => [
      item.materialName,
      item.shippingQuantity,
      item.receivedQuantity,
      "",
      item.backorderQuantity,
    ]),
    startY: materialTableStartY,
    margin: { left: 10, right: 10 },
    tableWidth: "auto",
    columnStyles: {
      0: { cellWidth: 68 },
      1: { cellWidth: 24 },
      2: { cellWidth: 32 },
      3: { cellWidth: 38 },
      4: { cellWidth: 28 },
    },
    didDrawCell: function (data) {
      const cell = data.cell;
      const doc = data.doc;

      if (data.section === "body" && data.column.index === 3) {
        const checkboxSize = 4;
        const x = cell.x + cell.width / 2 - checkboxSize / 2;
        const y = cell.y + cell.height / 2 - checkboxSize / 2;

        doc.setLineWidth(0.5);
        doc.rect(x, y, checkboxSize, checkboxSize);

        if (tableData[data.row.index]?.materialConform) {
          doc.line(x, y, x + checkboxSize, y + checkboxSize);
          doc.line(x + checkboxSize, y, x, y + checkboxSize);
        }

        doc.setLineWidth(0.2);
      }

      if (data.section === "body" || data.section === "head") {
        doc.line(
          cell.x + cell.width,
          cell.y,
          cell.x + cell.width,
          cell.y + cell.height,
        );

        if (data.column.index === 0) {
          doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
        }

        if (data.column.index === 1) {
          doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
        }

        if (data.column.index === 2) {
          doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
        }

        if (data.row.index === data.table.body.length - 1) {
          doc.line(
            cell.x,
            cell.y + cell.height,
            cell.x + cell.width,
            cell.y + cell.height,
          );
        }
      }
    },
  });

  let currentY = (pdf.lastAutoTable?.finalY || 120) + 10;

  if (currentY + 40 > pageHeight) {
    pdf.addPage();
    currentY = 10;
  }

  pdf.text("Note:", leftMargin, currentY);
  const noteLines = pdf.splitTextToSize(order?.comment || "", 130);
  if (noteLines.length > 0) {
    pdf.text(noteLines, leftColumn, currentY);
  }
  currentY += Math.max(noteLines.length, 1) * lineHeight;

  const materialComments = tableData
    .filter((item) => item.comment)
    .map((item) => `${item.materialName}: ${item.comment}`)
    .join("\n");
  const commentTitleY = currentY + 10;
  const commentSectionX = leftMargin;
  const commentSectionY = commentTitleY - 5;
  const commentSectionWidth = pageWidth - 2 * leftMargin;
  const commentLines = pdf.splitTextToSize(materialComments || "None", commentSectionWidth - 10);
  const commentSectionHeight = Math.max(30, commentLines.length * 5 + 12);

  const commentTitle = "COMMENTS:";
  const textWidth = pdf.getTextWidth(commentTitle);
  const commentTitleX = pageCenter - textWidth / 2;
  pdf.text(commentTitle, commentTitleX, commentTitleY);
  pdf.rect(
    commentSectionX,
    commentSectionY,
    commentSectionWidth,
    commentSectionHeight,
  );
  pdf.text(commentLines, commentSectionX + 5, commentSectionY + 12);

  const receptionBlockHeight = 2 * 10 + 2 * 10 + 10;

  if (
    commentSectionY + commentSectionHeight + receptionBlockHeight + 20 >
    pageHeight
  ) {
    pdf.addPage();
    currentY = 10;
  } else {
    currentY = commentSectionY + commentSectionHeight + 10;
  }

  const receptionText = "PERSON IN CHARGE OF RECEPTION:";
  const receptionTextWidth = pdf.getTextWidth(receptionText);
  const receptionTextX = pageCenter - receptionTextWidth / 2;
  const receptionTextY = currentY;
  pdf.text(receptionText, receptionTextX, receptionTextY);

  const inputFieldY = receptionTextY + 10;
  const interspace = 10;
  const receiverEmail = receiver.email || "";
  const receiverPhone = receiver.phone || receiver.telephone || receiver.mobile || "";

  pdf.text("Name:", 15, inputFieldY);
  pdf.text(receiverName || "N/A", 32, inputFieldY);
  pdf.text("Date:", 120, inputFieldY);
  pdf.text(formatPdfDate(shipment?.confirmedDeliveryDate), 137, inputFieldY);

  pdf.text("Email:", 15, inputFieldY + interspace);
  pdf.text(receiverEmail || "N/A", 32, inputFieldY + interspace);
  pdf.text("Signature:", 120, inputFieldY + interspace);

  pdf.text("Phone:", 15, inputFieldY + 2 * interspace);
  pdf.text(receiverPhone || "N/A", 32, inputFieldY + 2 * interspace);

  pdf.rect(
    leftMargin,
    receptionTextY - 5,
    commentSectionWidth,
    receptionBlockHeight + 4,
  );

  pdf.save(`AOR_${order?.orderNumber || order?.id || "order"}.pdf`);
};

export const printCdlTracking = (order, shipment, study, centerNumber) => {
  const pdf = new jsPDF("p", "mm", "a4"); // A4 size page of PDF
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();

  const pageCenter = pdf.internal.pageSize.getWidth() / 2;
  pdf.setFontSize(12);
  const text =
    "Shipping file of sampling kits, specific materials and documents";
  const textHeight = 12;
  const y = logoHeight + 30;
  const leftMargin = 10;
  const leftColumn = 60;
  const rectWidth = pageWidth - leftMargin * 2;
  const rightColumn = pageWidth / 2 + 50;

  function formatDate(isoDateString) {
    const date = new Date(isoDateString);
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}/${date.getFullYear()}`;
  }

  const formattedPreparationDate = formatDate(shipment.preparationDate);
  const formattedSendingDate = formatDate(shipment.sendingDate);
  const formattedCarrierOrderDate = formatDate(shipment.carrierOrderDate);
  const formattedOrderDate = formatDate(order.orderDate);
  const formattedDeliveryDate = shipment.deliveryDate
    ? formatDate(shipment.deliveryDate)
    : null;
  const formattedAorDate = shipment.confirmedDeliveryDate
    ? formatDate(shipment.confirmedDeliveryDate)
    : null;

  pdf.setFillColor(173, 216, 230); // Set fill color to light blue
  pdf.rect(leftMargin, y - textHeight, rectWidth, textHeight, "F"); // Draw a filled rectangle behind the text

  const textY = y - textHeight + pdf.internal.getLineHeight() / 2;
  pdf.text(text.toUpperCase(), pageCenter, textY, { align: "center" });

  pdf.setFontSize(10);
  const lineHeight = 7; // Adjusted line height for more spacing

  // First row: Study code + Sponsor name
  pdf.text(
    `${study?.sponsorStudyCode} (${study?.sponsor?.name})`,
    pageCenter,
    logoHeight + 35,
    { align: "center" },
  );

  // Second row: CDL Study Code
  const cdlStudyCodeText = `CDL Study Code: ${study.cdlStudyCode}`;
  pdf.text(cdlStudyCodeText, pageCenter, logoHeight + 35 + lineHeight, {
    align: "center",
  });

  // Center Number row
  pdf.text(`Center Number:`, leftMargin, logoHeight + 35 + 2 * lineHeight);
  pdf.text(centerNumber, leftColumn, logoHeight + 35 + 2 * lineHeight);

  // Center Protocols row
  pdf.text(`Center Protocols:`, leftMargin, logoHeight + 35 + 3 * lineHeight);
  pdf.text(
    getOrderCenterProtocols(order),
    leftColumn,
    logoHeight + 35 + 3 * lineHeight,
  );

  // Order Number row
  pdf.text(`Order Number:`, leftMargin, logoHeight + 35 + 4 * lineHeight);
  pdf.text(order?.orderNumber, leftColumn, logoHeight + 35 + 4 * lineHeight);

  // Client Order Date row
  pdf.text(`Client Order Date:`, leftMargin, logoHeight + 35 + 5 * lineHeight);
  pdf.text(formattedOrderDate, leftColumn, logoHeight + 35 + 5 * lineHeight);

  // Recipient row
  const recipient = order?.contactName || "Unknown";
  pdf.text(`Recipient:`, leftMargin, logoHeight + 35 + 6 * lineHeight);
  pdf.text(recipient, leftColumn, logoHeight + 35 + 6 * lineHeight);

  // Country row
  const addressParts = order.shippingAddress.split(",");
  const country = addressParts[addressParts.length - 1].trim();
  pdf.text(`Country:`, leftMargin, logoHeight + 35 + 7 * lineHeight);
  pdf.text(country, leftColumn, logoHeight + 35 + 7 * lineHeight);

  const tableData = order?.Supplies?.map((supply) => {
    const materialName = supply.Material.name;
    const centerProtocol =
      order?.Center?.Protocols?.map((protocol) => protocol.reference).join(
        ", ",
      ) || "";
    const shippingQuantity =
      shipment?.productions?.filter(
        (production) => production.materialId === supply.Material.id,
      ).length || 0;

    const productionsWithSameMaterial =
      shipment?.productions?.filter(
        (production) => production.materialId === supply.Material.id,
      ) || [];
    const selectedProductions = productionsWithSameMaterial
      .map((production) => {
        return `(${production.batchNumber}) ${production.reference} ${
          production.Protocols && production.Protocols.length > 0
            ? `[${production.Protocols.map((p) => p.reference).join(", ")}${production.cdlComment ? " - " + production.cdlComment : ""}]`
            : production.cdlComment
              ? `[${production.cdlComment}]`
              : ""
        } `;
      })
      .join("— ");

    return {
      materialName,
      shippingQuantity,
      selectedProductions,
      centerProtocol,
    };
  });

  const materialTableStartY = addBackorderMaterialBanner(
    pdf,
    shipment,
    logoHeight + 35 + 8 * lineHeight,
  );

  pdf.autoTable({
    head: [["Material", "Sent Quantity", "Selected Productions", "Protocol"]],
    body: tableData.map((item) => [
      item.materialName,
      item.shippingQuantity,
      item.selectedProductions,
      item.centerProtocol,
    ]),
    startY: materialTableStartY,
    margin: { left: 10, right: 10 },
    columnStyles: {
      0: { cellWidth: 56, overflow: "linebreak" },
      1: { cellWidth: 18, overflow: "linebreak" },
      2: { cellWidth: 74, overflow: "linebreak" },
      3: { cellWidth: 42, overflow: "linebreak" },
    },
  });

  // Get the y-coordinate of the bottom of the table
  let currentY = pdf.autoTable.previous.finalY + 10;

  // Check if there is enough space for the next section, if not, add a new page
  if (currentY + 40 > pdf.internal.pageSize.getHeight()) {
    pdf.addPage();
    currentY = 10; // Reset Y position for the new page
  }

  // PACKAGES
  const packageDetails = shipment.packageDetails.match(
    /pkg \d+: \d+ l, \d+ h, \d+ w, \d+(\.\d+)? vw/g,
  );
  // Convert into an array of objects with length, height, width, and volumetric weight
  const packages = packageDetails?.map((detail) => {
    const parts = detail.split(" ");
    return {
      length: parseInt(parts[2]),
      height: parseInt(parts[4]),
      width: parseInt(parts[6]),
      volumetricWeight: parseFloat(parts[8]),
    };
  });
  // Define the headers for the packages table
  const packageHeaders = [
    ["Package", "Length", "Height", "Width", "Volumetric Weight"],
  ];

  // Map the packages data to the table rows
  const packageRows = packages?.map((pkg, index) => [
    `Package ${index + 1}`,
    `${pkg.length || ""} cm`,
    `${pkg.height || ""} cm`,
    `${pkg.width || ""} cm`,
    `${pkg.volumetricWeight || ""} kg`,
  ]);

  // Add the packages table to the PDF
  pdf.autoTable({
    head: packageHeaders,
    body: packageRows,
    startY: currentY, // Start after the previous table
    margin: { left: 10, right: 10 },
    columnStyles: {
      0: { cellWidth: 50, overflow: "linebreak" },
      1: { cellWidth: 30, overflow: "linebreak" },
      2: { cellWidth: 30, overflow: "linebreak" },
      3: { cellWidth: 30, overflow: "linebreak" },
      4: { cellWidth: 50, overflow: "linebreak" },
    },
    didDrawPage: function (data) {
      // Reset currentY to the top of the new page
      currentY = data.settings.startY;
    },
  });

  currentY = pdf.autoTable.previous.finalY + 10;

  const checkboxSize = 3;

  // Row 1: Preparation date and Email to recipient
  pdf.text(`Preparation date:`, 10, currentY);
  pdf.text(formattedPreparationDate, leftColumn, currentY);
  pdf.text("Email to recipient:", 120, currentY);
  pdf.rect(rightColumn, currentY - checkboxSize, checkboxSize, checkboxSize); // Checkbox 'Yes'
  pdf.text("Yes", rightColumn + 5, currentY);
  pdf.rect(
    rightColumn + 20,
    currentY - checkboxSize,
    checkboxSize,
    checkboxSize,
  ); // Checkbox 'No'
  pdf.text("No", rightColumn + 25, currentY);

  currentY += lineHeight;

  // Row 2: Carrier order date and Email to client
  pdf.text(`Carrier order date:`, 10, currentY);
  pdf.text(formattedCarrierOrderDate, leftColumn, currentY);
  pdf.text("Email to client:", 120, currentY);
  pdf.rect(rightColumn, currentY - checkboxSize, checkboxSize, checkboxSize); // Checkbox 'Yes'
  pdf.text("Yes", rightColumn + 5, currentY);
  pdf.rect(
    rightColumn + 20,
    currentY - checkboxSize,
    checkboxSize,
    checkboxSize,
  ); // Checkbox 'No'
  pdf.text("No", rightColumn + 25, currentY);

  currentY += lineHeight;

  // Row 3: Sending date and Date of delivery
  pdf.text(`Sending date:`, 10, currentY);
  pdf.text(formattedSendingDate, leftColumn, currentY);
  pdf.text("Date of delivery:", 120, currentY);
  pdf.text(
    formattedDeliveryDate != null ? formattedDeliveryDate : "",
    rightColumn,
    currentY,
  );

  currentY += lineHeight;

  // Row 4: Shipped by and Date of AOR
  const shippedByName = `${shipment.shippedBy.firstname} ${shipment.shippedBy.name}`;
  pdf.text(`Shipped by:`, 10, currentY);
  pdf.text(shippedByName, leftColumn, currentY);
  pdf.text("Tracking CDL Portal:", 120, currentY);
  pdf.rect(rightColumn, currentY - checkboxSize, checkboxSize, checkboxSize); // Draw the checkbox

  currentY += lineHeight;

  // Row 5: Carrier name
  pdf.text(`Carrier name:`, 10, currentY);
  pdf.text(shipment.carrier, leftColumn, currentY);
  pdf.text("Date of AOR:", 120, currentY);
  pdf.text(
    formattedAorDate != null ? formattedAorDate : "",
    rightColumn,
    currentY,
  );

  currentY += lineHeight;

  // Row 6: Airwaybill
  pdf.text(`Airwaybill:`, 10, currentY);
  pdf.text(shipment.packingSlip, leftColumn, currentY);
  pdf.text("AOR on CDL Portal:", 120, currentY);
  pdf.rect(rightColumn, currentY - checkboxSize, checkboxSize, checkboxSize); // Draw the checkbox

  currentY += lineHeight;

  // Row 7: First supply
  pdf.text("First supply:", 10, currentY);
  pdf.rect(leftColumn, currentY - checkboxSize, checkboxSize, checkboxSize); // Draw the checkbox

  if (order.isFirstSupply) {
    // Draw an "X" inside the checkbox
    pdf.line(
      leftColumn,
      currentY - checkboxSize,
      leftColumn + checkboxSize,
      currentY,
    );
    pdf.line(
      leftColumn + checkboxSize,
      currentY - checkboxSize,
      leftColumn,
      currentY,
    );
  }

  pdf.text("Yes", leftColumn + 5, currentY);

  currentY += lineHeight;

  // Row 8: Comments
  if (order.comment && order.comment.trim() !== "") {
    pdf.text("Comment:", 10, currentY);

    const commentWidth = 130;
    const wrappedComment = pdf.splitTextToSize(order.comment, commentWidth);
    pdf.text(wrappedComment, leftColumn, currentY);
    currentY += wrappedComment.length * lineHeight;
  } else {
    currentY += lineHeight;
  }

  pdf.save(`cdltracking_${order.orderNumber}.pdf`);
};

export const printPackingList = (shipment, study, order, centerNumber) => {
  const pdf = new jsPDF("p", "mm", "a4"); // A4 size page of PDF
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();

  const pageCenter = pdf.internal.pageSize.getWidth() / 2;
  pdf.setFontSize(12);
  const text = "Packing List";
  const textHeight = 12;
  const y = logoHeight + 30;
  const leftMargin = 10;
  const rectWidth = pageWidth - leftMargin * 2;

  pdf.setFillColor(173, 216, 230); // Set fill color to light blue
  pdf.rect(leftMargin, y - textHeight, rectWidth, textHeight, "F"); // Draw a filled rectangle behind the text

  const textY = y - textHeight + pdf.internal.getLineHeight() / 2;
  pdf.text(text.toUpperCase(), pageCenter, textY, { align: "center" });
  const sendingDate = new Date(shipment.sendingDate);
  const formattedDate = `${sendingDate
    .getDate()
    .toString()
    .padStart(2, "0")}/${(sendingDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${sendingDate.getFullYear()}`;

  // Frame dimensions
  const frameX = pageCenter - 50;
  const frameY = logoHeight + 32;
  const frameWidth = 100;
  const centerProtocolLines = pdf.splitTextToSize(
    `Center Protocols: ${getOrderCenterProtocols(order)}`,
    frameWidth - 10,
  );
  const frameHeight = 33 + Math.max(centerProtocolLines.length - 1, 0) * 5;
  // Set the stroke color to black
  pdf.setDrawColor(0, 0, 0); // RGB color

  // Draw the frame
  pdf.rect(frameX, frameY, frameWidth, frameHeight).stroke();

  pdf.setFontSize(12);
  pdf.text(
    `${study.sponsorStudyCode} (${study.sponsor.name})`,
    pageCenter,
    logoHeight + 40,
    {
      align: "center",
    },
  );
  pdf.text(
    `CDL study code: ${study.cdlStudyCode}`,
    pageCenter,
    logoHeight + 45,
    {
      align: "center",
    },
  );
  // Set the font to bold
  pdf.setFont("helvetica", "bold");

  // Draw the text
  pdf.text(
    `Order ${order?.orderNumber} - ${formattedDate} `,
    pageCenter,
    logoHeight + 50,
    { align: "center" },
  );

  pdf.setFont("helvetica", "normal");
  pdf.text(`Center ${centerNumber}`, pageCenter, logoHeight + 55, {
    align: "center",
  });
  const centerProtocolY = logoHeight + 60;
  centerProtocolLines.forEach((line, index) => {
    pdf.text(line, pageCenter, centerProtocolY + index * 5, {
      align: "center",
    });
  });

  const tableData = order.Supplies
    ? order.Supplies.map((supply) => {
        const materialName = supply.Material.name;
        const centerProtocol =
          order?.Center?.Protocols?.map((protocol) => protocol.reference).join(
            ", ",
          ) || "";
        const shippingQuantity =
          shipment?.productions?.filter(
            (production) => production.materialId === supply.Material.id,
          ).length || 0;

        const productionsWithSameMaterial =
          shipment?.productions?.filter(
            (production) => production.materialId === supply.Material.id,
          ) || [];
        const selectedProductions = productionsWithSameMaterial
          .map(
            (production) =>
              `(${production.batchNumber}) ${production.reference}`,
          )
          .join(", ");

        return {
          materialName,
          shippingQuantity,
          selectedProductions,
          centerProtocol,
        };
      })
    : [];

  const materialTableStartY = addBackorderMaterialBanner(
    pdf,
    shipment,
    frameY + frameHeight + 10,
  );

  pdf.autoTable({
    head: [["Material", "Sent Quantity", "Selected Productions", "Protocol"]],
    body: tableData.map((item) => [
      item.materialName,
      item.shippingQuantity,
      item.selectedProductions,
      item.centerProtocol,
    ]),
    startY: materialTableStartY,
    margin: { left: 10, right: 10 },
    columnStyles: {
      0: { cellWidth: 56, overflow: "linebreak" },
      1: { cellWidth: 18, overflow: "linebreak" },
      2: { cellWidth: 74, overflow: "linebreak" },
      3: { cellWidth: 42, overflow: "linebreak" },
    },
    didDrawCell: function (data) {
      const cell = data.cell;
      const pdf = data.doc;

      // Draw vertical lines
      if (data.section === "body" || data.section === "head") {
        pdf.line(
          cell.x + cell.width,
          cell.y,
          cell.x + cell.width,
          cell.y + cell.height,
        );

        // Draw left border for the first column
        if (data.column.index === 0 || 1 || 2) {
          pdf.line(cell.x, cell.y, cell.x, cell.y + cell.height);
        }

        // Draw bottom border for the last row
        if (data.row.index === data.table.body.length - 1) {
          pdf.line(
            cell.x,
            cell.y + cell.height,
            cell.x + cell.width,
            cell.y + cell.height,
          );
        }
      }
    },
  });

  // Get the y-coordinate of the bottom of the table
  let currentY = pdf.autoTable.previous.finalY + 10;

  // Check if there is enough space for the next section, if not, add a new page
  if (currentY + 40 > pdf.internal.pageSize.getHeight()) {
    pdf.addPage();
    currentY = 10; // Reset Y position for the new page
  }

  pdf.save(`PackingList_${order.orderNumber}.pdf`);
};

export const printShippingLabel = (order, shipment) => {
  const doc = new jsPDF();

  const width = 50;
  const aspectRatio = 310 / 63; // image dimensions
  const height = width / aspectRatio;

  // Parameters: ImageData, Format, X, Y, Width, Height
  doc.addImage(logo, "PNG", 10, 10, width, height);

  // Sender
  doc.setFontSize(14);
  doc.text("SENDER:", 15, 40);

  // Draw underline for "SENDER:"
  doc.line(15, 42, 90, 42);

  doc.setFontSize(12);
  doc.text("CDL PHARMA ", 15, 50);
  doc.text("34 RUE PEYSSONNEL", 15, 55);
  doc.text("13003 MARSEILLE", 15, 60);
  doc.text("FRANCE", 15, 65);

  const senderNameY = 75;

  const senderName = `Name: ${shipment.shippedBy.firstname.toUpperCase()} ${shipment.shippedBy.name.toUpperCase()}`;
  const senderEmail = `Email: ${shipment.shippedBy.email}`;
  const senderNameLines = doc.splitTextToSize(senderName, 80);
  const senderEmailLines = doc.splitTextToSize(senderEmail, 80);

  doc.text(senderNameLines, 15, senderNameY);
  doc.text(
    "Phone: +33 04 96 20 96 25",
    15,
    senderNameY + senderNameLines.length * 5,
  );
  doc.text(senderEmailLines, 15, senderNameY + senderNameLines.length * 5 + 5);

  // Calculate sender block height
  const senderBlockHeight =
    60 + senderNameLines.length * 5 + senderEmailLines.length * 5;

  // Recipient
  doc.setFontSize(14);
  doc.text("RECIPIENT:", 100, 40);

  // Draw underline for "RECIPIENT:"
  doc.line(100, 42, 195, 42);

  doc.setFontSize(12);

  const addressLines = [
    `${order.hospital}`, // Hospital
    `${order.street}`, // Street
    order.ward ? `Ward: ${order.ward}` : "", // Ward (optional)
    order.building && order.floor
      ? `Building: ${order.building} / Floor: ${order.floor}`
      : order.building
        ? `Building: ${order.building}`
        : order.floor
          ? `Floor: ${order.floor}`
          : "", // Building and Floor (optional)
    `${order.zipcode} ${order.shippingAddress.split(",")[0]}`, // Zipcode + City
    `${order.shippingAddress.split(",")[1].trim()}`, // Country
    "", // Empty line to skip a row
    `${order.contactName ? `Name: ${order.contactName.toUpperCase()}` : ""}`, // Contact Name
    `Phone: ${order.contactPhone}`, // Contact Phone
    `Email: ${order.contactEmail}`, // Contact Email
  ];
  const filteredAddressLines = addressLines.filter((line) => line);

  let currentY = 50;
  let recipientBlockHeight = 0;
  filteredAddressLines.forEach((line, index) => {
    const isContactInfo =
      line.startsWith("Name:") ||
      line.startsWith("Phone:") ||
      line.startsWith("Email:");
    const maxWidth = 70; // max width for the text, if over it will wrap
    const textLines = doc.splitTextToSize(line, maxWidth); // Split text
    textLines.forEach((textLine) => {
      doc.text(
        isContactInfo ? textLine : textLine.toUpperCase(),
        100,
        currentY,
      );
      currentY += 5;
      recipientBlockHeight += 5;
    });
    // Add extra line height after the country
    if (line === `${order.shippingAddress.split(",")[1].trim()}`) {
      currentY += 5;
      recipientBlockHeight += 5;
    }
  });

  // Calculate the maximum height between sender and recipient blocks
  const maxHeight = Math.max(senderBlockHeight, recipientBlockHeight + 20);

  // Draw frame around the sender block with the calculated height
  doc.rect(10, 30, 85, maxHeight); // x, y, width, height

  // Draw frame around the recipient block with the calculated height
  doc.rect(96, 30, 105, maxHeight); // x, y, width, height

  doc.save(`ShippingLabel_${order.orderNumber}.pdf`); // save the PDF
};

export const printAorForm = (
  order,
  shipment,
  centerNumber,
  study,
  shippedProductions = [],
) => {
  const pdf = new jsPDF("p", "mm", "a4"); // A4 size page of PDF
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const pageCenter = pdf.internal.pageSize.getWidth() / 2;
  pdf.setFontSize(12);
  const text = "ACKNOWLEDGEMENT OF RECEIPT";
  const leftMargin = 10;
  const textHeight = 12;
  const y = logoHeight + 30;
  const leftColumn = 50;
  const headerWidth = pageWidth - leftMargin * 2;

  pdf.setFillColor(173, 216, 230);
  pdf.rect(leftMargin, y - textHeight, headerWidth, textHeight, "F");

  const textY = y - textHeight + pdf.internal.getLineHeight() / 2;
  pdf.text(text, pageCenter, textY, { align: "center" });

  pdf.setFontSize(10);

  const addressParts = order.shippingAddress.split(", ");
  const city = addressParts[0];
  const country = addressParts[1];
  const hospital = order.hospital;

  const lineHeight = 7;

  // Study row
  pdf.text(
    `${study.sponsorStudyCode} (${study.sponsor.name})`,
    pageCenter,
    logoHeight + 35,
    { align: "center" },
  );

  // CDL Study Code row
  const cdlStudyCodeText = `CDL Study Code: ${study.cdlStudyCode}`;
  pdf.text(cdlStudyCodeText, pageCenter, logoHeight + 35 + lineHeight, {
    align: "center",
  });

  // Order Number row
  pdf.text(`Order Number:`, leftMargin, logoHeight + 35 + 2 * lineHeight);
  pdf.text(order.orderNumber, leftColumn, logoHeight + 35 + 2 * lineHeight);

  // Center Number row
  pdf.text(`Center Number:`, leftMargin, logoHeight + 35 + 3 * lineHeight);
  pdf.text(centerNumber, leftColumn, logoHeight + 35 + 3 * lineHeight);

  // Center Protocols row
  pdf.text(`Center Protocols:`, leftMargin, logoHeight + 35 + 4 * lineHeight);
  pdf.text(
    getOrderCenterProtocols(order),
    leftColumn,
    logoHeight + 35 + 4 * lineHeight,
  );

  // Hospital row
  pdf.text(`Hospital:`, leftMargin, logoHeight + 35 + 5 * lineHeight);
  pdf.text(hospital, leftColumn, logoHeight + 35 + 5 * lineHeight);

  // City row
  pdf.text("City:", leftMargin, logoHeight + 35 + 6 * lineHeight);
  pdf.text(city, leftColumn, logoHeight + 35 + 6 * lineHeight);

  // Country row
  pdf.text(`Country:`, leftMargin, logoHeight + 35 + 7 * lineHeight);
  pdf.text(country, leftColumn, logoHeight + 35 + 7 * lineHeight);

  // Define the rectangle's position and dimensions
  const rectX = leftMargin;
  const rectY = logoHeight + 87;
  const rectWidth = pdf.internal.pageSize.getWidth() - 2 * leftMargin;
  const rectHeight = addReceiptInstructions(pdf, rectX, rectY, rectWidth);

  const tableData = order.Supplies
    ? order.Supplies.map((supply) => {
        const materialName = supply.Material.name;
        const shippingQuantity =
          shipment?.productions?.filter(
            (production) => production.materialId === supply.Material.id,
          ).length || 0;

        return {
          materialName,
          shippingQuantity,
          receivedQuantity: "",
          materialConform: "", // checkbox is drawn separately
          backorderQuantity: getBackorderDisplayValue(
            supply,
            shippedProductions,
            "Complete",
          ),
        };
      })
    : [];
  const materialTableStartY = addBackorderMaterialBanner(
    pdf,
    shipment,
    rectY + rectHeight + 15,
  );

  pdf.autoTable({
    head: [
      [
        "Material Name",
        "Sent Quantity",
        "Received Quantity",
        "Material Conform?",
        "Backorder",
      ],
    ],
    body: tableData.map((item) => [
      item.materialName,
      item.shippingQuantity,
      item.receivedQuantity,
      item.materialConform,
      item.backorderQuantity,
    ]),
    startY: materialTableStartY,
    margin: { left: 10, right: 10 },
    tableWidth: "auto", // Set the table width to auto
    columnStyles: {
      0: { cellWidth: 68 }, // Material Name
      1: { cellWidth: 24 }, // Sent Quantity
      2: { cellWidth: 32 }, // Received Quantity
      3: { cellWidth: 38 }, // Material Conform?
      4: { cellWidth: 28 }, // Backorder
    },
    didDrawCell: function (data) {
      const cell = data.cell;
      const pdf = data.doc;

      // Draw checkboxes
      if (data.section === "body" && data.column.index === 3) {
        const checkboxSize = 4;
        const x = cell.x + cell.width / 2 - checkboxSize / 2;
        const y = cell.y + cell.height / 2 - checkboxSize / 2;

        // Set the line width to make the checkbox lines thicker
        pdf.setLineWidth(0.5);

        // Draw the rectangle for the checkbox
        pdf.rect(x, y, checkboxSize, checkboxSize);

        // Reset the line width to default after drawing the checkbox
        pdf.setLineWidth(0.2);
      }

      // Draw vertical lines
      if (data.section === "body" || data.section === "head") {
        pdf.line(
          cell.x + cell.width,
          cell.y,
          cell.x + cell.width,
          cell.y + cell.height,
        );

        // Draw left border for the first column
        if (data.column.index === 0) {
          pdf.line(cell.x, cell.y, cell.x, cell.y + cell.height);
        }

        // Draw border between "Material Name" and "Sent Quantity"
        if (data.column.index === 1) {
          pdf.line(cell.x, cell.y, cell.x, cell.y + cell.height);
        }

        // Draw left border for the third column
        if (data.column.index === 2) {
          pdf.line(cell.x, cell.y, cell.x, cell.y + cell.height);
        }

        // Draw bottom border for the last row
        if (data.row.index === data.table.body.length - 1) {
          pdf.line(
            cell.x,
            cell.y + cell.height,
            cell.x + cell.width,
            cell.y + cell.height,
          );
        }
      }
    },
  });

  // Get the y-coordinate of the bottom of the table
  let currentY = pdf.autoTable.previous.finalY + 10;

  // Check if there is enough space for the next section, if not, add a new page
  if (currentY + 40 > pageHeight) {
    pdf.addPage();
    currentY = 10; // Reset Y position for the new page
  }
  // Add the order.comment as a note
  pdf.text("Note:", leftMargin, currentY);
  const commentWidth = 130;
  const wrappedComment = pdf.splitTextToSize(order.comment, commentWidth);
  pdf.text(wrappedComment, leftColumn, currentY);
  currentY += 5;

  // Define the position and dimensions of the comment section
  const commentTitleY = currentY + 10;
  const commentSectionX = leftMargin;
  const commentSectionY = commentTitleY - 5;
  const commentSectionWidth = pdf.internal.pageSize.getWidth() - 2 * leftMargin;
  const commentSectionHeight = 30;

  // Add the title
  const commentTitle = "COMMENTS:";
  const textWidth = pdf.getTextWidth(commentTitle);
  const commentTitleX = pageCenter - textWidth / 2;
  pdf.text(commentTitle, commentTitleX, commentTitleY);

  // Draw the comment section
  pdf.rect(
    commentSectionX,
    commentSectionY,
    commentSectionWidth,
    commentSectionHeight,
  );

  // Calculate the height of the "PERSON IN CHARGE OF RECEPTION" block
  const receptionBlockHeight = 2 * 10 + 2 * 10 + 10; // Adjusted height to include the title and input fields

  // Check if there is enough space for the "PERSON IN CHARGE OF RECEPTION" block, if not, add a new page
  if (
    commentSectionY + commentSectionHeight + receptionBlockHeight + 20 >
    pageHeight
  ) {
    pdf.addPage();
    currentY = 10; // Reset Y position for the new page
  } else {
    currentY = commentSectionY + commentSectionHeight + 10;
  }

  // Add the "PERSON IN CHARGE OF RECEPTION" text
  const receptionText = "PERSON IN CHARGE OF RECEPTION:";
  const receptionTextWidth = pdf.getTextWidth(receptionText);
  const receptionTextX = pageCenter - receptionTextWidth / 2;
  const receptionTextY = currentY;
  pdf.text(receptionText, receptionTextX, receptionTextY);

  // Define the position of the input fields
  const inputFieldY = receptionTextY + 10;
  const interspace = 10; // space between rows

  pdf.text("Name:", 15, inputFieldY);
  pdf.text("Date:", 120, inputFieldY);

  pdf.text("Email: ", 15, inputFieldY + interspace);
  pdf.text("Signature:", 120, inputFieldY + interspace);

  pdf.text("Phone:", 15, inputFieldY + 2 * interspace);

  // Draw the rectangle around the "PERSON IN CHARGE OF RECEPTION" block
  pdf.rect(
    leftMargin,
    receptionTextY - 5,
    commentSectionWidth,
    receptionBlockHeight + 4,
  );

  pdf.save(`AorForm_${order.orderNumber}.pdf`);
};
